import {
  Directive,
  ElementRef,
  HostListener,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges,
  inject,
} from '@angular/core';
import { Platform } from '@angular/cdk/platform';
import { MatTooltip } from '@angular/material/tooltip';

const INFO_TOOLTIP_ICON_CLASSES = [
  'access-tooltip-icon',
  'title-tooltip-icon',
  'transient-filters-info-icon',
];

const INFO_TOOLTIP_ICON_NAMES = new Set(['help_outline', 'info_outline', 'info']);

/**
 * Makes MatTooltip usable with a single tap on touch devices.
 * Material's default long-press + hide-on-touchend behavior is disabled;
 * tap toggles the tooltip, and a second tap or a tap elsewhere closes it.
 *
 * Set `matTooltipMobileTap="off"` on navigational / paint hosts to disable mobile
 * tooltips while keeping desktop hover. Primary-action hosts (buttons, links,
 * menu triggers, action icons) keep Material long-press instead of tap-toggle.
 */
@Directive({
  selector: '[matTooltip]',
  standalone: true,
})
export class MatTooltipMobileTapDirective implements OnInit, OnChanges {
  @Input('matTooltipMobileTap') mobileTap: 'on' | 'off' = 'on';

  private readonly tooltip = inject(MatTooltip, { self: true });
  private readonly platform = inject(Platform);
  private readonly elementRef = inject(ElementRef<HTMLElement>);
  private readonly useTapTogglePlatform = this.isTapTogglePlatform();
  private skipClickToggle = false;

  ngOnInit(): void {
    this.applyMobileTooltipMode();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!this.useTapTogglePlatform || !changes['mobileTap'] || changes['mobileTap'].firstChange) {
      return;
    }

    this.applyMobileTooltipMode();
  }

  @HostListener('click', ['$event'])
  onClick(event: Event): void {
    if (!this.useTapTogglePlatform || this.mobileTap === 'off') {
      return;
    }

    if (this.tooltip.disabled || !this.tooltip.message) {
      return;
    }

    // Re-check on every tap: a button may mount disabled (tap-toggle armed) and
    // later enable; cached skipClickToggle must not steal the action click.
    if (this.isPrimaryActionHost()) {
      this.skipClickToggle = true;
      return;
    }

    if (this.skipClickToggle) {
      return;
    }

    event.stopPropagation();
    this.tooltip.toggle();
  }

  private applyMobileTooltipMode(): void {
    if (!this.useTapTogglePlatform) {
      return;
    }

    // Explicit opt-out: no long-press and no tap-to-toggle on mobile.
    if (this.mobileTap === 'off') {
      this.tooltip.touchGestures = 'off';
      this.skipClickToggle = true;
      return;
    }

    // Host already opted out of gestures for its own show/hide logic.
    if (this.tooltip.touchGestures === 'off') {
      this.skipClickToggle = true;
      return;
    }

    // Primary actions keep Material long-press; tap must not toggle the tip.
    if (this.isPrimaryActionHost()) {
      this.skipClickToggle = true;
      return;
    }

    // Must run before MatTooltip.ngAfterViewInit binds long-press listeners.
    this.tooltip.touchGestures = 'off';
    this.skipClickToggle = false;
  }

  private isTapTogglePlatform(): boolean {
    if (this.platform.IOS || this.platform.ANDROID) {
      return true;
    }

    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return false;
    }

    return window.matchMedia('(hover: none) and (pointer: coarse)').matches;
  }

  private isPrimaryActionHost(): boolean {
    const el = this.elementRef.nativeElement;
    const uiButtonHost = el.closest('app-ui-button');
    if (uiButtonHost) {
      return !uiButtonHost.classList.contains('ui-button-disabled');
    }

    if (el.tagName === 'BUTTON' || el.tagName === 'A' || el.getAttribute('role') === 'button') {
      return !(el as HTMLButtonElement).disabled;
    }

    if (this.hasNavigationOrMenuTrigger(el)) {
      return true;
    }

    return el.tagName === 'MAT-ICON' && !this.isInformationalTooltipIcon(el);
  }

  private hasNavigationOrMenuTrigger(el: HTMLElement): boolean {
    return (
      el.hasAttribute('routerLink') ||
      el.hasAttribute('ng-reflect-router-link') ||
      el.hasAttribute('href') ||
      el.hasAttribute('matMenuTriggerFor') ||
      el.hasAttribute('ng-reflect-menu-trigger-for') ||
      el.classList.contains('mat-menu-trigger')
    );
  }

  private isInformationalTooltipIcon(el: HTMLElement): boolean {
    if (INFO_TOOLTIP_ICON_CLASSES.some((className) => el.classList.contains(className))) {
      return true;
    }

    const iconName = el.textContent?.trim() ?? '';
    return INFO_TOOLTIP_ICON_NAMES.has(iconName) && !this.hasNavigationOrMenuTrigger(el);
  }
}
